import type { LocationPriceInput, ProductDetail, ProductLocationPrice, ProductStatus } from './vendorApiClient';
import { centsToInput, parseMoney } from './money';

/**
 * The "Prices & availability" form on a product: one row per location, held as
 * the text the person typed until they save.
 */

export interface PriceRowForm {
  storeId: string;
  price: string;
  sale: string;
  saleStartsOn: string;
  saleEndsOn: string;
  inStock: boolean;
  shown: boolean;
}

export type PriceRowProblem = 'priceInvalid' | 'saleInvalid' | 'saleNeedsPrice' | 'saleNotBelowPrice' | 'saleEndInPast' | 'saleEndsBeforeStart';

export function toRowForm(l: ProductLocationPrice): PriceRowForm {
  return {
    storeId: l.storeId,
    price: centsToInput(l.priceCents),
    sale: centsToInput(l.salePriceCents),
    saleStartsOn: l.saleStartsOn ?? '',
    saleEndsOn: l.saleEndsOn ?? '',
    inStock: l.inStock,
    shown: l.shown,
  };
}

export type RowsCheck =
  | { ok: true; input: LocationPriceInput[] }
  | { ok: false; problems: Record<string, PriceRowProblem> };

/**
 * A blank price means "not sold at this location". A sale needs a price above
 * it; an end date needs a sale, cannot already have passed, and cannot come
 * before the sale starts. A blank start means today.
 */
export function checkRows(rows: PriceRowForm[], today: string): RowsCheck {
  const problems: Record<string, PriceRowProblem> = {};
  const input: LocationPriceInput[] = [];
  for (const r of rows) {
    const price = parseMoney(r.price);
    const sale = parseMoney(r.sale);
    if (!price.ok) {
      problems[r.storeId] = 'priceInvalid';
      continue;
    }
    if (!sale.ok) {
      problems[r.storeId] = 'saleInvalid';
      continue;
    }
    if (sale.cents != null && price.cents == null) {
      problems[r.storeId] = 'saleNeedsPrice';
      continue;
    }
    if (sale.cents != null && price.cents != null && sale.cents >= price.cents) {
      problems[r.storeId] = 'saleNotBelowPrice';
      continue;
    }
    const endsOn = sale.cents != null && r.saleEndsOn ? r.saleEndsOn : null;
    if (endsOn && endsOn < today) {
      problems[r.storeId] = 'saleEndInPast';
      continue;
    }
    const startsOn = sale.cents != null && r.saleStartsOn ? r.saleStartsOn : null;
    if (endsOn && startsOn && endsOn < startsOn) {
      problems[r.storeId] = 'saleEndsBeforeStart';
      continue;
    }
    input.push({
      storeId: r.storeId,
      priceCents: price.cents,
      salePriceCents: sale.cents,
      saleStartsOn: startsOn,
      saleEndsOn: endsOn,
      inStock: r.inStock,
      shown: r.shown,
    });
  }
  return Object.keys(problems).length > 0 ? { ok: false, problems } : { ok: true, input };
}

export function rowsDirty(rows: PriceRowForm[], saved: ProductLocationPrice[]): boolean {
  const byStore = new Map(saved.map((l) => [l.storeId, toRowForm(l)]));
  return rows.some((r) => {
    const s = byStore.get(r.storeId);
    return (
      !s ||
      s.price !== r.price.trim() ||
      s.sale !== r.sale.trim() ||
      (r.sale.trim() !== '' && s.saleStartsOn !== r.saleStartsOn) ||
      s.saleEndsOn !== r.saleEndsOn ||
      s.inStock !== r.inStock ||
      s.shown !== r.shown
    );
  });
}

/** Copies one location's price, sale and switches onto every other row. */
export function copyRowToAll(rows: PriceRowForm[], fromStoreId: string): PriceRowForm[] {
  const from = rows.find((r) => r.storeId === fromStoreId);
  if (!from) return rows;
  return rows.map((r) => (r.storeId === fromStoreId ? r : { ...from, storeId: r.storeId }));
}

/** What a new sale's start box holds when the person types the first sale price. */
export function withSaleStart(row: PriceRowForm, sale: string, today: string): Partial<PriceRowForm> {
  return sale.trim() !== '' && row.sale.trim() === '' && !row.saleStartsOn ? { sale, saleStartsOn: today } : { sale };
}

/**
 * One status for the product as a whole (the header chip). A question from us
 * outranks everything, because it hides the product at every location; then
 * our staff's check of a new product.
 */
export function overallStatus(detail: Pick<ProductDetail, 'blockState' | 'locations'> & { review?: ProductDetail['review'] }): ProductStatus {
  if (detail.blockState === 'questioned') return 'needs_answer';
  if (detail.blockState === 'handed_to_glp') return 'with_us';
  if (detail.blockState === 'unresolvable') return 'stuck';
  if (detail.review?.status === 'returned') return 'returned';
  if (detail.review?.status === 'pending') return 'verifying';
  const priced = detail.locations.filter((l) => l.priceCents != null);
  if (priced.length === 0) return 'not_sold_here';
  const shown = priced.filter((l) => l.shown);
  if (shown.length === 0) return 'hidden';
  return shown.some((l) => l.status === 'live') ? 'live' : 'location_pending';
}
